import { css } from '@emotion/core'
import { quint } from '@entrata/portals-common/lib/styled-utils'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'
import getProjectConditions from 'portal-modules/common/utils/get-project-conditions'
const { isRP, isTP } = getProjectConditions()
import { getEmbeddedVendorName } from 'portal-modules/common/utils/get-embedded-vendor-name'
import { RESPONSIVE_BREAKPOINTS, DIMENSIONS_PX } from 'portal-modules/tenant-portal-layout/constants/responsive-breakpoints'
import {
  CAPRICON_VENDOR,
} from 'portal-modules/common/constants/keys'

// Font family mapping
const getFontFamilyValue = (fontType) => {
  const fontFamilies = {
    'sans-serif': `system-ui, -apple-system, sans-serif`,
    'serif': `Georgia, "Times New Roman", serif`,
    'monospace': `"Courier New", Courier, monospace`
  }
  return fontFamilies[fontType] || fontFamilies['sans-serif']
}

// Font size multiplier mapping
const getFontSizeMultiplier = (sizeType) => {
  const sizeMultipliers = {
    'small': 0.875,   // 87.5% - 14px if base is 16px
    'medium': 1,      // 100% - 16px
    'large': 1.125,   // 112.5% - 18px
    'xl': 1.25        // 125% - 20px
  }
  return sizeMultipliers[sizeType] || sizeMultipliers['medium']
}

// Font weight mapping
const getFontWeightValue = (weightType) => {
  const fontWeights = {
    'light': 100,    // Thin for maximum contrast
    'regular': 400,  // Normal
    'bold': 700      // Bold
  }
  return fontWeights[weightType] || fontWeights['regular']
}

const getIconStrokeWidth = (iconStyleType) => {
  const strokeWidths = {
    'minimal': 1,   // Thin stroke
    'outline': 1.5,      // Standard stroke
    'filled': 2        // Medium stroke for filled icons
  }
  return strokeWidths[iconStyleType] || strokeWidths['outline']
}

export default (theme, customFontFamily = 'sans-serif', customFontSize = 'medium', customFontWeight = 'regular', customIconStyle = 'outline', customSpacingDensity = 'comfortable') => {
  const fontFamily = getFontFamilyValue(customFontFamily)
  const fontSizeMultiplier = getFontSizeMultiplier(customFontSize)
  const fontWeight = getFontWeightValue(customFontWeight)
  const iconStrokeWidth = getIconStrokeWidth(customIconStyle)

  // Calculate spacing scale based on density
  const spacingScale = customSpacingDensity === 'compact' ? 1 : 1.5;

  return css`
  /* CSS Variables for reusable values - Responsive Design */
  :root {
    --spacing-scale: ${spacingScale};
    --font-weight: ${fontWeight};
    --form-field-bg: ${theme.backgroundColor?.tp_card || '#252e42'};
    --form-field-border: ${theme.fields?.border_color || '#2a3349'};
    --form-field-focus-border: ${theme.fields?.focus || '#a1a7c4'};
    --form-field-text: ${theme.text?.medium || 'white'};
    --form-field-placeholder: ${theme.text?.muted_foreground || '#a1a7c4'};
    --form-field-radius: 8px;
    --form-field-padding: 16px 12px;
    --form-field-height: 48px;
    --form-field-font-size: 16px;
    --error-color: ${theme.error?.base || '#ef4444'};
    --icon-stroke-width: ${iconStrokeWidth};
    /* Tenant Portal Enhanced Responsive Variables */
    --tp-mobile-padding: 16px;
    --tp-tablet-padding: 20px;
    --tp-desktop-padding: 24px;
    --tp-mobile-header-height: 60px;
    --tp-tablet-header-height: 64px;
    --tp-desktop-header-height: 57px;
    --tp-sidebar-width-mobile: ${DIMENSIONS_PX.SIDEBAR_WIDTH_MOBILE};
    --tp-sidebar-width-tablet: 320px;
    
    /* Mobile responsive adjustments */
    @media ${RESPONSIVE_BREAKPOINTS.mobile.query} {
      --form-field-padding: 20px 16px;
      --form-field-height: 56px;
      --form-field-font-size: 16px; /* Prevents zoom on iOS */
      --form-field-radius: 12px;
    }
    
    /* Tablet responsive adjustments */
    @media (min-width: 768px) and (max-width: 1024px) {
      --form-field-padding: 18px 14px;
      --form-field-height: 52px;
      --form-field-font-size: 15px;
      --form-field-radius: 10px;
    }
    
    /* Desktop adjustments */
    @media (min-width: 1025px) {
      --form-field-padding: 14px 12px;
      --form-field-height: 44px;
      --form-field-font-size: 15px;
    }
  }

  * {
    box-sizing: border-box;
  }

  html,
  body {
    min-height: 100%;
    background: transparent;
    color: ${theme.text.dark};
    font-family: ${fontFamily};
    font-size: calc(16px * ${fontSizeMultiplier});
    font-weight: ${fontWeight};
    letter-spacing: 0.248px;
    margin: 0;
    padding: 0;

    /*  Added style for ios safari only */
    @supports (-webkit-overflow-scrolling: touch) {
      position: fixed;
      overflow: scroll;
      height: 100%;
      width: 100vw;
      -webkit-overflow-scrolling: touch;
    }
  }

  body a,
  button,
  input[type='checkbox' i],
  input[type='radio' i],
  select {
    cursor: pointer;
  }

  body,
  a {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  /* Enhanced Tenant Portal specific responsive styles */
  /* Mobile optimizations for Tenant Portal */
  @media ${RESPONSIVE_BREAKPOINTS.mobile.query} {
    body {
      padding-top: var(--tp-mobile-header-height);
      font-size: 16px; /* Prevent zoom on iOS */
    }
    
    /* Improved touch targets */
    button, 
    a[role="button"], 
    input[type="submit"], 
    input[type="button"] {
      min-height: 44px;
      min-width: 44px;
    }
    
    /* Better spacing for mobile */
    .tp-mobile-spacing {
      padding: var(--tp-mobile-padding);
      margin-bottom: 16px;
    }
  }
  
  /* Tablet optimizations for Tenant Portal */
  @media (min-width: 768px) and (max-width: 1024px) {
    body {
      padding-top: var(--tp-tablet-header-height);
    }
    
    /* Optimized spacing for tablet */
    .tp-tablet-spacing {
      padding: var(--tp-tablet-padding);
      margin-bottom: 20px;
    }
  }
  
  /* Desktop optimizations for Tenant Portal */
  @media (min-width: 1025px) {
    body {
      padding-top: 0;
    }
    
    /* Desktop spacing */
    .tp-desktop-spacing {
      padding: var(--tp-desktop-padding);
      margin-bottom: 24px;
    }
  }

  /* width */
  ::-webkit-scrollbar {
    width: 8px;
    height: 5px;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    ${'' /* background: ${gray.light}; */}
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    background: ${theme.gray.gray2};
    border-radius: 100px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${theme.gray.light};
  }

  #bg-img,
  #bg-overlay {
    width: 100vw;
    height: 100vh;
    position: fixed;
    top: 0;
    left: 0;
    z-index: -1;

    ::before,
    ::after {
      content: '';
      width: 100%;
      height: 100%;
      display: block;
      position: fixed;
      top: 0;
      left: 0;
    }

    ::after {
      background-image: url(${theme.backgroundUrl});
      background-size: cover;
      opacity: 0.1;
    }

    ::before {
      background: ${getEmbeddedViewFlag()
      ? theme.embedded.background_color
      : isTP
        ? (theme.backgroundColor?.light || theme.white?.base || '#151c2c')
        : theme.primary.base};

      @media only screen and (max-width: 768px) {
        background: ${getEmbeddedViewFlag()
      ? theme.embedded.background_color
      : theme.mode.isDark && !window?.location?.pathname?.includes('/auth')
        ? 'transparent'
        : isTP
          ? (theme.backgroundColor?.light || theme.white?.base || '#151c2c')
          : theme.primary.base};
      }

      opacity: ${getEmbeddedViewFlag() && 0.7};
      right: 0;
      bottom: 0;
    }

    &.blur::after {
      filter: blur(5px);
    }
  }


  .root {
    min-height: 100vh;
    position: relative;
    transition:
      transform 400ms ${quint.easeOut},
      opacity 150ms ${quint.easeOut};
  }

  #app-container {
    transition:
      transform 300ms ${quint.easeOut},
      opacity 300ms ${quint.easeOut};
  }

  h2 {
    font-size: 16px;
    margin: 1em 0;
  }

  p {
    line-height: 1.5;
  }

  .blur-bg {
    > *:not(:last-child) {
      filter: blur(5px);
      opacity: 0.5;
      overflow: hidden;
    }
  }

  img {
    position: relative;

    :before {
      content: '';
      width: 100%;
      height: 100%;
      background-color: ${theme.gray.highlight};
      border: 1px solid ${theme.gray.light};
      display: block;
      position: absolute;
      top: 0;
      left: 0;
    }

    :after {
      content: 'Broken Image';
      width: 100%;
      color: ${theme.text.light};
      display: block;
      font-size: 12px;
      position: absolute;
      top: 50%;
      left: 0;
      text-align: center;
      transform: translateY(-50%);
    }
  }

  form {
    max-width: 704px;
    width: 100%;
    
    @media only screen and (max-width: 768px) {
      max-width: 100%;
      padding: 0 16px;
    }
    
    @media only screen and (min-width: 1024px) {
      max-width: 600px;
    }
  }
  #root button {
    font-family: ${fontFamily};
  }
  #root label {
    font-family: ${fontFamily};
    ${getEmbeddedViewFlag() && { color: theme.secondary.base }};
  }


  /* Error message styling and spacing */
  .error,
  .error-message,
  .validation-error,
  [class*="error"],
  .form-field .error,
  .boxed-field .error,
  .field-error,
  .invalid-feedback,
  [role="alert"] {
    display: block !important;
    margin-top: 8px !important;
    margin-bottom: 12px !important;
    padding: 4px 0 !important;
    font-size: 12px !important;
    line-height: 1.4 !important;
    color: var(--error-color) !important;
    font-weight: 500 !important;
    font-family: ${fontFamily} !important;
    
    @media only screen and (max-width: 768px) {
      font-size: 14px !important;
      margin-top: 12px !important;
      margin-bottom: 16px !important;
    }
  }

  /* Ensure proper spacing for required field indicators */
  .required,
  [class*="required"] {
    margin-top: 6px !important;
    margin-bottom: 8px !important;
  }

  /* Specific spacing for validation messages that appear below inputs */
  input + .error,
  input + .error-message,
  input + .validation-error,
  input + [class*="error"],
  textarea + .error,
  textarea + .error-message,
  textarea + .validation-error,
  textarea + [class*="error"],
  select + .error,
  select + .error-message,
  select + .validation-error,
  select + [class*="error"] {
    margin-top: 10px !important;
    margin-bottom: 16px !important;
    clear: both !important;
  }

  /* Additional spacing for form field containers with validation */
  .form-field.has-error,
  .boxed-field.has-error,
  .form-group.has-error,
  [class*="field"].has-error {
    margin-bottom: 24px !important;
  }

  /* InternationalPhoneField specific error spacing globally */
  .boxed-field > .form-field > span.error {
    display: block !important;
    margin-top: 10px !important;
    padding-top: 4px !important;
    clear: both !important;
    position: relative !important;
    z-index: 10 !important;
  }
    
  /* Ensure the inner form-field has enough bottom padding for error messages */
  .boxed-field > .form-field {
    padding-bottom: 8px !important;
    min-height: fit-content !important;
    margin-bottom: -24px !important;
  }

  /* Icon and text spacing fixes - Responsive */
  /* Navigation items with icons */
  .nav-item,
  .menu-item,
  [class*="nav-"],
  [class*="menu-"] {
    display: flex !important;
    align-items: center !important;
    gap: 12px !important;
    
    @media only screen and (max-width: 768px) {
      gap: 16px !important;
      padding: 12px 16px !important;
    }
    
    @media only screen and (min-width: 1024px) {
      gap: 10px !important;
    }
  }
  svg {
      stroke-width: var(--icon-stroke-width) !important;
      fill: currentColor !important;
    }
  /* Icon spacing in navigation and buttons */
  .nav-item svg,
  .menu-item svg,
  .nav-link svg,
  .button svg,
  [class*="nav-"] svg,
  [class*="menu-"] svg,
  [class*="btn"] svg {
    margin-right: 8px !important;
    margin-left: 0 !important;
    flex-shrink: 0 !important;
  }

  /* Text spacing after icons */
  svg + span,
  svg + div,
  svg + p,
  svg + a,
  svg + text,
  .icon + span,
  .icon + div,
  .icon + p,
  .icon + a,
  .icon + text {
    margin-left: 8px !important;
    display: inline-block !important;
  }

  /* Specific spacing for icon-text combinations */
  [class*="icon-text"],
  [class*="text-icon"],
  .icon-label,
  .label-icon {
    display: flex !important;
    align-items: center !important;
    gap: 10px !important;
  }

  /* Button icon spacing */
  button svg,
  .button svg,
  [role="button"] svg {
    margin-left: 0 !important;
  }

  /* List items with icons */
  li svg,

  /* Ensure proper alignment for icon containers */
  .icon-container,
  [class*="icon-wrapper"] {
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  /* Sidebar and navigation specific fixes */
  .sidebar-item,
  .nav-sidebar-item,
  [class*="sidebar"] li,
  [class*="navigation"] li {
    display: flex !important;
    align-items: center !important;
    padding: 8px 12px !important;
    gap: 12px !important;
  }

  /* Fix for overlapping icons in navigation panels */
  .nav-panel svg,
  .sidebar-panel svg,
  [class*="panel"] svg {
    min-width: 20px !important;
    width: 20px !important;
    height: 20px !important;
    margin-right: 10px !important;
    flex-shrink: 0 !important;
  }

  /* Text content after icons should have proper spacing */
  .nav-text,
  .menu-text,
  [class*="nav-"] .text,
  [class*="menu-"] .text {
    margin-left: 0 !important;
    padding-left: 0 !important;
    flex: 1 !important;
  }

  /* Prevent icon and text from overlapping in flex containers */
  .nav-item > *,
  .menu-item > *,
  [class*="nav-"] > *,
  [class*="menu-"] > * {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }

  .nav-item > *:not(:last-child),
  .menu-item > *:not(:last-child),
  [class*="nav-"] > *:not(:last-child),
  [class*="menu-"] > *:not(:last-child) {
    margin-right: 8px !important;
  }
  /* UNIVERSAL FORM FIELD STYLING - Consistent across all UI */
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"]),
  textarea,
  select,
  option {
    background: var(--form-field-bg) !important;
    background-color: var(--form-field-bg) !important;
    background-image: none !important;
    color: var(--form-field-text) !important;
    -webkit-text-fill-color: var(--form-field-text) !important;
    border: 1px solid var(--form-field-border) !important;
    border-radius: var(--form-field-radius) !important;
    font-size: var(--form-field-font-size) !important;
    font-family: ${fontFamily} !important;
    line-height: 1.6 !important;
    box-sizing: border-box !important;
    box-shadow: none !important;
    text-shadow: none !important;
    outline: none !important;
    transition: all 0.2s ease !important;
    min-height: var(--form-field-height) !important;
    vertical-align: top !important;
    
    ${getEmbeddedViewFlag() && {
      'background-color': theme.embedded.foreground_color + ' !important',
      color: theme.primary.base + ' !important',
      'border-color': theme.embedded.input_border_outline + ' !important',
      '-webkit-text-fill-color': theme.primary.base + ' !important'
    }};
  }

  /* TENANT PORTAL FORM FIELD OVERRIDE - Prevent double borders */
  /* Override component-specific borders to prevent double borders */
  .styled-text-field,
  .styled-text-field input,
  .input-wrapper,
  .input-wrapper input,
  .boxed-field,
  .boxed-field input,
  .boxed-field textarea,
  .boxed-field select,
  [class*="StyledTextField"],
  [class*="StyledTextField"] input,
  [class*="InputWrapper"],
  [class*="InputWrapper"] input {
    border: none !important;
    border-width: 0 !important;
    box-shadow: none !important;
  }
  
  /* Ensure wrapper containers don't add extra borders */
  .form-field,
  .form-field-wrapper,
  [class*="form-field"],
  [class*="FormField"] {
    border: none !important;
  }
  
  /* DROPDOWN OPTION SPECIFIC STYLING - Additional browser compatibility */
  option {
    -webkit-appearance: none !important;
    -moz-appearance: none !important;
    appearance: none !important;
    border: none !important;
  }
  
  /* Ensure dropdown options in all contexts use consistent styling */
  select option,
  .dropdown-field option,
  .boxed-field-dropdown option,
  [class*="dropdown"] option,
  [class*="Dropdown"] option {
    color: var(--form-field-text) !important;
    background: var(--form-field-bg) !important;
    background-color: var(--form-field-bg) !important;
    -webkit-text-fill-color: var(--form-field-text) !important;
    border: none !important;
  }

  /* Focus states - Only change border color, don't add new borders */
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"]):focus,
  textarea:focus,
  select:focus,
  option:hover,
  option:focus,
  option:checked {
    background: var(--form-field-bg) !important;
    background-color: var(--form-field-bg) !important;
    color: var(--form-field-text) !important;
    -webkit-text-fill-color: var(--form-field-text) !important;
    border-color: var(--form-field-focus-border) !important;
    border-width: 1px !important; /* Ensure only 1px border width */
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1) !important;
    outline: none !important;
  }

  /* TENANT PORTAL FOCUS STATE OVERRIDES - Prevent component-specific focus borders */
  .styled-text-field:focus,
  .styled-text-field input:focus,
  .input-wrapper:focus,
  .input-wrapper input:focus,
  .boxed-field:focus,
  .boxed-field input:focus,
  .boxed-field textarea:focus,
  .boxed-field select:focus,
  [class*="StyledTextField"]:focus,
  [class*="StyledTextField"] input:focus,
  [class*="InputWrapper"]:focus,
  [class*="InputWrapper"] input:focus {
    border: 1px solid var(--form-field-focus-border) !important;
    border-width: 1px !important;
    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.1) !important;
  }

  /* Placeholder styles */
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"])::placeholder,
  textarea::placeholder {
    color: var(--form-field-placeholder) !important;
    opacity: 0.7 !important;
  }

  /* Autofill styles */
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"]):-webkit-autofill,
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"]):-webkit-autofill:hover,
  input:not([type="radio"]):not([type="checkbox"]):not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="hidden"]):not([type="file"]):-webkit-autofill:focus,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus {
    -webkit-box-shadow: 0 0 0 1000px var(--form-field-bg) inset !important;
    -webkit-text-fill-color: var(--form-field-text) !important;
    transition: background-color 5000s ease-in-out 0s !important;
  }

  /* Consolidated font-family declarations */
  #root input,
  #root textarea,
  #root span,
  #root p,
  #root a,
  #root li,
  #root div {
    font-family: ${fontFamily};
  }
  
  #root select option {
    font-family: ${fontFamily};
    background-color: ${theme.mode.isDark && theme.mode.background_color};
    color: ${theme.mode.isDark && theme.mode.text_color};
  }
  #root .boxed-field,
  [role='dialog'] .boxed-field {
    background-color: var(--form-field-bg) !important;
    font-family: ${fontFamily};
    border-radius: var(--form-field-radius) !important;
    border: 1px solid var(--form-field-border) !important;
    color: var(--form-field-text) !important;
    min-height: var(--form-field-height) !important;
    ${getEmbeddedViewFlag() && {
      'background-color': theme.embedded.foreground_color + ' !important',
      color: theme.primary.base + ' !important',
      'border-color': theme.embedded.input_border_outline + ' !important',
    }};
    .i18n-country-field {
      border-top-left-radius: 8px;
      border-bottom-left-radius: 8px;
    }
    .i18n-phone-field {
      border-top-right-radius: 8px;
      border-bottom-right-radius: 8px;
    }
    button {
      :first-child {
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }
      :last-child {
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      }
    }
  }

  .text-area-field {
    div,
    textarea {
      background-color: var(--form-field-bg) !important;
      color: var(--form-field-text) !important;
      border-radius: var(--form-field-radius) !important;
      font-size: var(--form-field-font-size) !important;
      line-height: 1.6 !important;
      min-height: var(--form-field-height) !important;
      vertical-align: top !important;
      &:-internal-autofill-selected {
        -webkit-box-shadow: 0 0 0px 1000px var(--form-field-bg) inset !important;
      }
      &:-internal-autofill-previewed {
        -webkit-box-shadow: 0 0 0px 1000px var(--form-field-bg) inset !important;
      }
      -webkit-text-fill-color: var(--form-field-text) !important;
    }
  }

  .form-field {
    label {
      color: ${getEmbeddedViewFlag() && getEmbeddedVendorName() == CAPRICON_VENDOR && theme.primary.base};
    }
  }

  .cc-card-field {
    overflow: hidden;
  }

  ${'' /* Overirding css for picker field for mode  */}
  .boxed-field.picker-field {
    button {
      background-color: ${theme.mode.isDark && 'transparent'};
    }
    button[aria-selected='true'] {
      background-color: ${theme.mode.isDark && theme.mode.button_text};
    }
  }

  ${'' /* react-office-viewer adds hidden attribute, overriding it */}
  input[type="text"][hidden].boxed-field {
    display: inline-block !important;
  }

  &.i18n-phone-field {
    background-color: ${theme.mode.isDark && 'transparent'};
  }
  &.i18n-country-field {
    background-color: ${theme.mode.isDark && 'transparent'};
  }
`
}